import React, {ComponentProps} from 'react';

import {Colors, Text} from '@optimaxdev/design-system';

import {logWithSentry} from 'libs/sentry/sentry';
import {AlgoliaHitMappedType} from 'types/successPage';

import s from './smartDoctorSuggestions.scss';

const convertToLowercase = (value?: string) => {
    if (!value) return '';
    try {
        return value?.toLowerCase();
    } catch (err) {
        logWithSentry(err, `<Suggestion /> Failed to convert ${value} to lowercase.`);
        return '';
    }
};

const isDesktop = __BUILD_TYPE__ === 'desktop';

const getCommonSuggestionTextProps = (
    isSelected: boolean,
): Partial<ComponentProps<typeof Text>> => {
    const textColor = isSelected ? Colors.nevada : Colors.limedSpruce;
    return {
        className: s.capitalize,
        color: isDesktop ? textColor : Colors.blueCharcoal,
        lineHeight: isDesktop ? '20px' : 'normal',
        size: isDesktop ? '14px' : '16px',
    };
};
/**
 * Component for render dr details options in autocomplete component
 * @param {PropsType} props - props type
 * @returns {React.ReactElement} Rendered react component
 */
export const Suggestion = ({
    firstName,
    isSelected = false,
    lastName,
    mailingAddress1stLine,
    mailingAddressCity,
    mailingAddressState,
    mailingAddressZipCode,
    organizationName,
}: AlgoliaHitMappedType & {isSelected?: boolean}) => {
    return (
        <div className={s.suggestion}>
            <Text
                {...getCommonSuggestionTextProps(isSelected)}
                color={isDesktop && isSelected ? Colors.nevada : Colors.blueCharcoal}
                bold
            >
                {convertToLowercase(organizationName) || (
                    <>
                        {convertToLowercase(firstName)} {convertToLowercase(lastName)}
                    </>
                )}
            </Text>
            {organizationName && (
                <Text {...getCommonSuggestionTextProps(isSelected)}>
                    {convertToLowercase(firstName)} {convertToLowercase(lastName)}
                </Text>
            )}
            <Text {...getCommonSuggestionTextProps(isSelected)}>
                {convertToLowercase(mailingAddress1stLine)}
            </Text>
            <Text {...getCommonSuggestionTextProps(isSelected)}>
                {convertToLowercase(mailingAddressCity)},{' '}
                {(mailingAddressState || '').toUpperCase()}{' '}
                {convertToLowercase(mailingAddressZipCode)}
            </Text>
        </div>
    );
};

const commonNoResultTextProps: Partial<ComponentProps<typeof Text>> = {
    as: 'p',
    color: isDesktop ? Colors.limedSpruce : Colors.blueCharcoal,
    lineHeight: isDesktop ? '22px' : 'normal',
    size: isDesktop ? '14px' : '16px',
};

/**
 * Component for render no result component
 * @param {React.MouseEventHandler<HTMLElement>} onClick handler
 * @returns {React.ReactElement} Rendered react component
 */
export const NoResultComponent = ({onClick}: {onClick: React.MouseEventHandler<HTMLElement>}) => {
    return (
        <div className={s.noResultWrapper}>
            <Text {...commonNoResultTextProps}>Can’t find your eye doctor?</Text>
            <button className={s.btn} onClick={onClick} type="button">
                <Text {...commonNoResultTextProps} color={Colors.blueCharcoal} bold underline>
                    Enter it manually
                </Text>
            </button>
        </div>
    );
};
